import { CheckCircle, Info, Warning, WarningCircle, X } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';
import { Button } from './Button';

const TONE = {
  info: {
    wrap: 'bg-info-50 border-info-500/30 text-info-700 dark:bg-info-700/15 dark:text-info-500',
    Icon: Info,
  },
  success: {
    wrap: 'bg-success-50 border-success-500/30 text-success-700 dark:bg-success-700/15 dark:text-success-500',
    Icon: CheckCircle,
  },
  warning: {
    wrap: 'bg-warning-50 border-warning-500/30 text-warning-700 dark:bg-warning-700/15 dark:text-warning-500',
    Icon: Warning,
  },
  danger: {
    wrap: 'bg-danger-50 border-danger-500/30 text-danger-700 dark:bg-danger-700/15 dark:text-danger-500',
    Icon: WarningCircle,
  },
  neutral: {
    wrap: 'bg-muted border-border text-foreground',
    Icon: Info,
  },
};

/**
 * Banner persistente (ds/banner). Distinto do toast (sonner).
 *
 * @param {{
 *   tone?: 'info'|'success'|'warning'|'danger'|'neutral',
 *   title: React.ReactNode,
 *   description?: React.ReactNode,
 *   action?: { label: string, onClick?: () => void },
 *   onDismiss?: () => void,
 *   className?: string,
 * }} props
 */
export function Banner({
  tone = 'info',
  title,
  description,
  action,
  onDismiss,
  className,
}) {
  const config = TONE[tone] || TONE.info;
  const Icon = config.Icon;
  const dismissable = Boolean(onDismiss) && (tone === 'info' || tone === 'success' || tone === 'neutral');

  return (
    <div
      role="status"
      data-ds-id={`ds/banner--${tone}`}
      className={cn(
        'flex items-start gap-space-3 rounded-ds-xl border p-space-4',
        config.wrap,
        className,
      )}
    >
      <Icon className="size-5 shrink-0" weight="fill" aria-hidden />
      <div className="min-w-0 flex-1 space-y-space-2">
        <p className="text-type-body font-semibold">{title}</p>
        {description ? (
          <p className="text-type-body-sm text-muted-foreground">{description}</p>
        ) : null}
        {action?.label ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={action.onClick}
            className="rounded-ds-full px-space-3 py-space-1-5 text-type-label"
          >
            {action.label}
          </Button>
        ) : null}
      </div>
      {dismissable ? (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-ds-full p-space-1 text-muted-foreground hover:bg-card/60 hover:text-foreground"
          aria-label="Fechar"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export default Banner;
